import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { getBridgeLaunchStatus } from '../../services/bridgeLaunchStatus';
import { getBridgeEndpoint } from '../../services/bridgeEndpoint';
import type { BridgeCause } from '../../utils/bridge/bridgeCause';
import { buildBridgeDiagnostics, copyToClipboard } from '../../utils/bridge/bridgeDiagnostics';

const RESULT_VISIBLE_MS = 60_000;

interface CopyDiagnosticsButtonProps {
    cause: BridgeCause;
}

/**
 * Turns "it does not work" into something support can read. Without it the facts that decide the
 * diagnosis — which bridge, which port, what it says at startup — are only visible in a devtools
 * console the user will not open.
 */
export const CopyDiagnosticsButton: React.FC<CopyDiagnosticsButtonProps> = ({ cause }) => {
    const { t } = useTranslation();
    const bridgeInfo = useAcombaBridgeStore((s) => s.bridgeInfo);
    const [outcome, setOutcome] = useState<'idle' | 'copied' | 'failed'>('idle');
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Clears the copied/failed line on its own — it isn't a permanent status, and leaving it up
    // forever reads as if the page still had something to say about the last attempt.
    useEffect(() => () => {
        if (hideTimeoutRef.current !== null) clearTimeout(hideTimeoutRef.current);
    }, []);

    const handleCopy = async () => {
        const text = buildBridgeDiagnostics({
            cause,
            health: bridgeInfo,
            launched: getBridgeLaunchStatus(),
            attemptedUrl: getBridgeEndpoint(),
            now: new Date(),
            userAgent: navigator.userAgent,
        });

        setOutcome(await copyToClipboard(text) ? 'copied' : 'failed');
        if (hideTimeoutRef.current !== null) clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => setOutcome('idle'), RESULT_VISIBLE_MS);
    };

    return (
        // Column, not a row: the confirmation belongs directly under the button it answers, not
        // beside it where a longer message would crowd the button rather than read as its own line.
        <div className="flex flex-col items-center gap-xs text-center">
            <LinkButton
                data-testid="acomba-bridge-copy-diagnostics-btn"
                onClick={() => void handleCopy()}
            >
                {t(TranslationKey.AcombaBridge.Cause.CopyDiagnostics)}
            </LinkButton>

            {outcome !== 'idle' && (
                <span
                    data-testid="acomba-bridge-copy-diagnostics-result"
                    role="status"
                    className="text-s text-content-secondary"
                >
                    {t(outcome === 'copied'
                        ? TranslationKey.AcombaBridge.Cause.CopyDiagnosticsDone
                        : TranslationKey.AcombaBridge.Cause.CopyDiagnosticsFailed)}
                </span>
            )}
        </div>
    );
};
